import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Add as AddIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from "@mui/icons-material";
import config from "../config.json";
import { apiRequest } from "../functions/apiRequest";
import FullImageModal from "../blog/FullImageModal";
import BlogCard from "../blog/BlogCard";
import SingleBlogPost from "../blog/SingleBlogPost";
import BlogEditorModal from "../blog/BlogEditorModal";

const HOST_NAME = process.env.REACT_APP_HOST_NAME;
const themeConfig = (HOST_NAME && config[HOST_NAME]?.css) || {
  buttonColor: "#967bb6",
  buttonFontColor: "#ffffff",
  websiteColor: "#967bb6",
};

export default function Blog() {
  const { id } = useParams();
  const navigate = useNavigate();

  // User, Token & Permissions
  const token = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = localStorage.getItem("userID") ? parseInt(localStorage.getItem("userID")) : user?.id;
  const userEmail = localStorage.getItem("email_id");
  const isAdmin = user?.is_superuser || user?.is_staff || user?.roles?.includes("admin") || userEmail === "admin";
  const [canAuthor, setCanAuthor] = useState(false);
  const canCreate = isAdmin || canAuthor || user?.can_create_blog || user?.roles?.includes("blog_author");
  const canEditPost = (post) => isAdmin || (post && String(post.author_id) === String(userId));

  // State
  const [posts, setPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, title: "" });

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [formData, setFormData] = useState({ title: "", snippet: "", content: "", domain: HOST_NAME || "all", images: [] });

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const queryParam = HOST_NAME ? `?domain=${encodeURIComponent(HOST_NAME)}` : "";
      const data = await apiRequest("get", null, `/blogs${queryParam}`, token);
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Unable to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSinglePost = async (postId) => {
    setLoading(true);
    try {
      const data = await apiRequest("get", null, `/blogs/${postId}`, token);
      setActivePost(data || null);
    } catch (err) {
      setError("Post not found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchSinglePost(id);
    else { setActivePost(null); fetchPosts(); }
  }, [id]);

  useEffect(() => {
    if (token && userId && !isAdmin) {
      apiRequest("get", null, `/user/profile/${userId}`, token, null, {}, true)
        .then((res) => {
          const profile = res?.data?.response?.response_data || res?.data || res;
          if (profile?.can_create_blog || profile?.roles?.includes("blog_author")) {
            setCanAuthor(true);
          }
        })
        .catch(() => {});
    }
  }, [token, userId, isAdmin]);

  const handleOpenCreateModal = () => {
    setEditingPostId(null);
    setFormData({ title: "", snippet: "", content: "", domain: HOST_NAME || "all", images: [] });
    setModalOpen(true);
  };

  const handleOpenEditModal = (post) => {
    setEditingPostId(post.id);
    const safeDomain = post.domain && !post.domain.includes("localhost") ? post.domain : "all";
    setFormData({ title: post.title, snippet: post.snippet || "", content: post.content, domain: safeDomain, images: post.images || [] });
    setModalOpen(true);
  };

  const handleSavePost = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert("Please provide both a Title and Content.");
      return;
    }
    try {
      const payload = { ...formData, target_domain: formData.domain || "all" };
      if (editingPostId) {
        const updated = await apiRequest("put", JSON.stringify(payload), `/blogs/${editingPostId}`, token);
        setPosts((prev) => prev.map((p) => (p.id === editingPostId ? updated : p)));
        if (activePost?.id === editingPostId) setActivePost(updated);
      } else {
        const created = await apiRequest("post", JSON.stringify(payload), "/blogs", token);
        setPosts((prev) => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (err) {
      alert("Error saving blog post.");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await apiRequest("delete", null, `/blogs/${postId}`, token);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (activePost?.id === postId) navigate("/blogspot");
    } catch (err) {
      alert("Error deleting blog post.");
    }
  };

  const handleAddComment = async (content) => {
    try {
      const newC = await apiRequest("post", JSON.stringify({ content }), `/blogs/${activePost.id}/comment`, token);
      setActivePost((prev) => ({ ...prev, comments: [...(prev.comments || []), newC] }));
    } catch (err) {
      alert("Error adding comment.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await apiRequest("delete", null, `/blogs/comment/${commentId}`, token);
      setActivePost((prev) => ({ ...prev, comments: prev.comments.filter((c) => c.id !== commentId) }));
    } catch (err) {
      alert("Error deleting comment.");
    }
  };

  const handleImageClick = (images, index, title) => {
    setLightbox({ open: true, images, index, title });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <Box style={{ backgroundColor: "#f9f9f9", minHeight: "100vh", padding: "20px 16px 60px 16px" }}>
      <Box style={{ maxWidth: "800px", margin: "0 auto 20px auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: "1.4rem", color: "#1a1a1a" }}>
          {activePost ? "Story Details" : "Latest Updates"}
        </Typography>
        {canCreate && !activePost && (
          <Button variant="contained" onClick={handleOpenCreateModal} startIcon={<AddIcon />} style={{ backgroundColor: themeConfig.buttonColor, color: themeConfig.buttonFontColor, textTransform: "none", fontWeight: 700, borderRadius: "20px" }}>
            New Post
          </Button>
        )}
      </Box>

      {loading && <Box display="flex" justifyContent="center" my={6}><CircularProgress style={{ color: themeConfig.buttonColor }} /></Box>}
      {error && <Typography align="center" color="error" style={{ margin: "30px 0" }}>{error}</Typography>}

      {!loading && activePost && (
        <SingleBlogPost post={activePost} canEdit={canEditPost(activePost)} isAdmin={isAdmin} userId={userId} token={token} onBack={() => navigate("/blogspot")} onEdit={handleOpenEditModal} onDelete={handleDeletePost} onAddComment={handleAddComment} onDeleteComment={handleDeleteComment} onImageClick={handleImageClick} themeConfig={themeConfig} />
      )}

      {!loading && !activePost && (
        <Box style={{ maxWidth: "800px", margin: "0 auto" }}>
          {posts.length === 0 ? (
            <Typography align="center" color="textSecondary" my={4}>No stories published yet.</Typography>
          ) : (
            posts.map((post) => (
              <BlogCard key={post.id} post={post} canEdit={canEditPost(post)} onEdit={handleOpenEditModal} onDelete={handleDeletePost} onReadMore={() => navigate(`/blogspot/${post.id}`)} onImageClick={handleImageClick} themeConfig={themeConfig} />
            ))
          )}
        </Box>
      )}

      <Box style={{ position: "fixed", bottom: "28px", right: "28px", zIndex: 99999 }}>
        <Button variant="contained" onClick={scrollToTop} size="small" startIcon={<KeyboardArrowUpIcon />} style={{ backgroundColor: themeConfig.buttonColor, color: themeConfig.buttonFontColor, borderRadius: "24px", textTransform: "none", fontFamily: "'Raleway', sans-serif", fontWeight: 700, padding: "7px 18px", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" }}>
          Top
        </Button>
      </Box>

      <FullImageModal isOpen={lightbox.open} onClose={() => setLightbox({ ...lightbox, open: false })} images={lightbox.images} initialIndex={lightbox.index} title={lightbox.title} />
      <BlogEditorModal open={modalOpen} isEditing={Boolean(editingPostId)} formData={formData} setFormData={setFormData} onClose={() => setModalOpen(false)} onSave={handleSavePost} themeConfig={themeConfig} />
    </Box>
  );
}
